import { useState } from 'react';
import { ExternalLink, FilePlus2, RotateCcw } from 'lucide-react';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import RoleGate from '../../components/RoleGate';

// NestJS sends `message` as a string, or as an array of them for validation errors
function errorText(err, fallback) {
  const message = err.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return (typeof message === 'string' && message) || err.message || fallback;
}

// Run links come from GitHub through quality-service; only ever link to GitHub
const runUrl = (url) => (typeof url === 'string' && url.startsWith('https://github.com/') ? url : null);

function RunLink({ url }) {
  const href = runUrl(url);
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="str-stp-run-link">
      <ExternalLink size={12} /> View run
    </a>
  );
}

// Shown where "View STP" would be, for an epic that has no STP. `request` is the epic's latest
// Generate STP request from quality-service (epicData.stpRequest), kept up to date by the STP
// Generator run's webhook, so it survives refreshes and is the same for everyone:
//   none / not_started -> Generate STP (writers)   requested / running -> progress
//   failed -> blocked until an admin resets it      succeeded -> waiting for the STP link
// The page renders it with key={epicKey}, so a request still pending for the previous epic can only
// update the unmounted instance.
function StpGeneration({ epicKey, request }) {
  // What this user just did, until the next refresh brings the server's view
  const [local, setLocal] = useState(null); // null | 'sending' | 'requested' | 'resetting' | 'reset'
  const [error, setError] = useState('');
  const url = `${QUALITY_SERVICE_API_URL}/hub/software-test-report/${encodeURIComponent(epicKey)}/stp`;

  const status = local === 'requested' ? 'requested' : local === 'reset' ? null : request?.status ?? null;

  const generate = async () => {
    setLocal('sending');
    setError('');
    try {
      await api.post(url);
      setLocal('requested');
    } catch (err) {
      setLocal(null);
      setError(errorText(err, 'Could not start the STP generation'));
    }
  };

  const reset = async () => {
    setLocal('resetting');
    setError('');
    try {
      await api.delete(url);
      setLocal('reset');
    } catch (err) {
      setLocal(null);
      setError(errorText(err, 'Could not reset the STP request'));
    }
  };

  const errorLine = error && (
    <p className="str-epic-error" role="alert">
      {error}
    </p>
  );
  const by = request?.requestedBy && local !== 'requested' ? ` (requested by ${request.requestedBy})` : '';

  if (status === 'requested' || status === 'running') {
    return (
      <div className="str-stp-generate">
        <p className="str-stp-status" role="status">
          {status === 'running' ? 'STP generation in progress' : 'STP generation requested, waiting for the run to start'}
          {by}. It takes a few minutes: use refresh to check for it.
        </p>
        <RunLink url={request?.workflowRunUrl} />
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="str-stp-generate">
        <p className="str-stp-status str-stp-failed" role="status">
          STP generation failed{by}. An admin has to reset it before it can run again.
        </p>
        <RunLink url={request?.workflowRunUrl} />
        <RoleGate allow={['admin']}>
          <button type="button" className="str-link str-stp-button" onClick={reset} disabled={local === 'resetting'}>
            <RotateCcw size={16} /> {local === 'resetting' ? 'Resetting…' : 'Reset'}
          </button>
        </RoleGate>
        {errorLine}
      </div>
    );
  }

  if (status === 'succeeded') {
    return (
      <div className="str-stp-generate">
        <p className="str-stp-status" role="status">
          STP generated. Its link appears here once it is linked to the epic: refresh in a minute.
        </p>
        <RunLink url={request?.workflowRunUrl} />
      </div>
    );
  }

  // No request, or one whose run never started
  return (
    <div className="str-stp-generate">
      <RoleGate allow={['admin', 'write']}>
        <button type="button" className="str-link str-stp-button" onClick={generate} disabled={local === 'sending'}>
          <FilePlus2 size={16} /> {local === 'sending' ? 'Starting…' : 'Generate STP'}
        </button>
      </RoleGate>
      {status === 'not_started' && (
        <p className="str-stp-status">The last STP request never started a run. You can try again.</p>
      )}
      {errorLine}
    </div>
  );
}

export default StpGeneration;
